import { InferResponseType } from "hono/client";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { UploadCloudIcon, UserRound, XIcon } from "lucide-react";
import { Nullish } from "utility-types";
import { useAuth } from "../contexts";
import { getI18n } from "../contexts/i18n";
import { AsyncStatus } from "../helpers/asyncStatus";
import { client } from "../helpers";
import { cn } from "../tailwind-utils";
import { InlineSpinner, UploadProgress } from "./Loading";

type ClientImageUpload = (typeof client.api)["images"]["upload"];
type ClientSitePostResponse = InferResponseType<ClientImageUpload["$post"]>;

export interface ImageUploadMetadata {
  usage: "hero" | "photo" | "team_member";
  width: number;
  height: number;
  size_bytes: number;
  content_type: string;
  original_size_bytes: number;
  original_content_type: string;
  original_filename: string;
}

interface ImageUploadProps {
  className?: string;
  variant?: "default" | "avatar";
  usage?: "hero" | "photo" | "team_member";
  image?: string | Nullish;
  onChange?: (params: { uri: string; metadata?: ImageUploadMetadata }) => void;
  onDelete?: () => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = (props) => {
  const auth = useAuth();
  const i18n = getI18n(auth.user?.language);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const processingTimeoutRef = useRef<number | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);
  const [imageFile, setImageFile] = useState<File | Nullish>(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<AsyncStatus>("idle");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastFailedFile, setLastFailedFile] = useState<File | Nullish>(null);
  const isUploading = status === "loading";
  const isBusy = isUploading || isProcessing;

  const resetStatus = () => {
    setStatus("idle");
    setUploadError(null);
    setIsProcessing(false);
    setProgress(0);
  };

  const failUpload = (message: string, file?: File | Nullish) => {
    setStatus("error");
    setUploadError(message);
    setIsProcessing(false);
    setProgress(0);
    setLastFailedFile(file || null);
  };

  const handleUpload = async (file: File) => {
    if (!file) return;
    if (!auth.token) {
      failUpload(i18n["Authentication required before upload."], file);
      return;
    }

    if (xhrRef.current && isUploading) {
      xhrRef.current.abort();
    }

    setImageFile(file);
    setStatus("loading");
    setUploadError(null);
    setIsProcessing(false);
    setProgress(0);
    setLastFailedFile(file);
    const formData = new FormData();
    formData.append("image", file);
    if (props.usage) {
      formData.append("usage", props.usage);
    }

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        setProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let response: ClientSitePostResponse;
        try {
          response = JSON.parse(xhr.responseText);
        } catch (error) {
          console.error(error);
          failUpload(
            i18n["Upload failed. Could not read server response."],
            file
          );
          return;
        }
        if (!response.success) {
          console.error(response.error);
          failUpload(i18n["Upload failed."], file);
          return;
        }
        setIsProcessing(true);
        setProgress(100);
        if (processingTimeoutRef.current) {
          window.clearTimeout(processingTimeoutRef.current);
        }
        if (props.onChange) {
          props.onChange({
            uri: response.data.image.uri,
            metadata: {
              usage: response.data.image.usage,
              width: response.data.image.width,
              height: response.data.image.height,
              size_bytes: response.data.image.size_bytes,
              content_type: response.data.image.content_type,
              original_size_bytes: response.data.image.original_size_bytes,
              original_content_type: response.data.image.original_content_type,
              original_filename: response.data.image.original_filename,
            },
          });
        }
        processingTimeoutRef.current = window.setTimeout(() => {
          setStatus("success");
          setUploadError(null);
          setIsProcessing(false);
          setImageFile(null);
          if (resetTimeoutRef.current) {
            window.clearTimeout(resetTimeoutRef.current);
          }
          resetTimeoutRef.current = window.setTimeout(() => {
            resetStatus();
          }, 500);
        }, 250);
      } else {
        console.error(`Error uploading file: ${xhr.statusText}`);
        failUpload(i18n["Upload failed. Please retry."], file);
      }
    });

    xhr.addEventListener("error", () => {
      console.error("Error uploading file");
      failUpload(i18n["Upload failed. Please retry."], file);
    });

    xhr.addEventListener("abort", () => {
      resetStatus();
    });

    xhr.open("POST", client.api.images.upload.$url());
    xhr.setRequestHeader("Authorization", `Bearer ${auth.token}`);
    xhr.send(formData);
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const [file] = e.target.files || [];
    if (!file) return;
    e.target.value = "";
    handleUpload(file);
  };

  const previewImageSrc = useMemo(
    () => (imageFile ? URL.createObjectURL(imageFile) : null),
    [imageFile]
  );
  useEffect(
    () => () => {
      if (previewImageSrc) {
        URL.revokeObjectURL(previewImageSrc);
      }
    },
    [previewImageSrc]
  );
  useEffect(
    () => () => {
      xhrRef.current?.abort();
      if (processingTimeoutRef.current) {
        window.clearTimeout(processingTimeoutRef.current);
      }
      if (resetTimeoutRef.current) {
        window.clearTimeout(resetTimeoutRef.current);
      }
    },
    []
  );

  const imageSrc = previewImageSrc || props.image;
  const isAvatar = props.variant === "avatar";

  const containerClass = isAvatar
    ? "h-full cursor-pointer hover:border-gray-700 border-gray-300 border hover:border-current border-dashed overflow-hidden rounded-full bg-gray-100 transition duration-300 ease-in-out"
    : "h-full cursor-pointer hover:border-gray-700 border-transparent border-2 hover:border-current border-dashed overflow-hidden rounded-lg transition duration-300 ease-in-out";

  const placeholderClass = isAvatar ? "bg-gray-100" : "bg-gray-50";
  return (
    <div className={cn("h-full w-full", props.className)}>
      <div className="h-full w-full relative">
        <input
          className="hidden"
          type="file"
          accept="image/png, image/jpeg, image/gif, image/webp"
          ref={fileInputRef}
          disabled={isBusy}
          onChange={handleChange}
        />

        <div
          aria-busy={isBusy}
          onClick={() => {
            if (isBusy) return;
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
              fileInputRef.current.click();
            }
          }}
          className={containerClass}
        >
          {imageSrc ? (
            <>
              <img
                src={imageSrc}
                alt={i18n["Image preview"]}
                data-testid="image-upload-preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                disabled={isBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setImageFile(null);
                  resetStatus();
                  if (props.onDelete) props.onDelete();
                }}
                className={cn(
                  "absolute top-1 right-1 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-sm ring-1 ring-black/5 w-[28%] h-[28%] min-w-6 min-h-6 max-w-8 max-h-8",
                  isBusy && "opacity-50 cursor-not-allowed"
                )}
                aria-label={i18n["Delete image"]}
              >
                <XIcon className="w-4 h-4 text-gray-700 hover:text-red-500" />
              </button>
            </>
          ) : (
            <div
              className={`w-full h-full flex items-center justify-center ${placeholderClass}`}
            >
              {isAvatar ? (
                <UserRound className="w-2/3 h-2/3 text-gray-400" />
              ) : (
                <UploadCloudIcon className="w-2/3 h-2/3 text-gray-400" />
              )}
            </div>
          )}
          {isBusy && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <InlineSpinner
                showLabel
                label={
                  isProcessing ? i18n["Processing..."] : i18n["Uploading..."]
                }
                className="rounded-md bg-white px-3 py-2 text-slate-700 shadow-sm"
              />
            </div>
          )}
          {(isUploading || isProcessing || status === "error") && (
            <div className="absolute inset-x-2 bottom-2">
              <UploadProgress
                progress={progress}
                status={
                  status === "error"
                    ? "error"
                    : isProcessing
                    ? "processing"
                    : "loading"
                }
                message={
                  uploadError ||
                  (isProcessing
                    ? i18n["Processing image..."]
                    : i18n["Uploading image..."])
                }
                onRetry={() => {
                  if (lastFailedFile) {
                    handleUpload(lastFailedFile);
                  }
                }}
                retryLabel={i18n["Retry"]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
